import React, {useMemo} from "react"
import PropTypes from "prop-types"
import {Box, Button, IconButton, Stack, Typography} from "@mui/material"
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded"
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded"
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded"
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded"
import SelectComponent from "../select/select-component"

export const getPaginationWindow = (page, totalPages, windowSize = 5) => {
    const safeTotal = Math.max(1, Number(totalPages) || 1)
    const safePage = Math.min(safeTotal, Math.max(1, Number(page) || 1))
    const size = Math.min(safeTotal, Math.max(1, Number(windowSize) || 1))
    const half = Math.floor(size / 2)
    let start = Math.max(1, safePage - half)
    const end = Math.min(safeTotal, start + size - 1)

    start = Math.max(1, end - size + 1)
    return Array.from({length: end - start + 1}, (_, index) => start + index)
}

const PageButton = ({page, active, disabled, onClick}) => (
    <Button
        aria-label={`Ir para a página ${page}`}
        aria-current={active ? "page" : undefined}
        disabled={disabled}
        onClick={onClick}
        sx={{
            minWidth: 40,
            width: 40,
            height: 40,
            p: 0,
            color: "common.white",
            border: "1px solid",
            borderColor: active ? "primary.main2" : "primary.white70",
            backgroundColor: active ? "primary.dark" : "transparent",
            "&:hover": {
                backgroundColor: active ? "primary.dark" : "primary.white10",
            },
        }}
    >
        {page}
    </Button>
)

PageButton.propTypes = {
    page: PropTypes.number.isRequired,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
}

const NavigationButton = ({label, disabled, onClick, children}) => (
    <IconButton
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        sx={{
            width: 40,
            height: 40,
            color: "common.white",
            border: "1px solid",
            borderColor: "primary.white70",
            borderRadius: 0,
            "&.Mui-disabled": {
                color: "primary.white30",
                borderColor: "primary.white10",
            },
        }}
    >
        {children}
    </IconButton>
)

NavigationButton.propTypes = {
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
}

const TablePagination = ({
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [25, 50, 100],
    disabled = false,
}) => {
    const safeTotal = Math.max(0, Number(totalItems) || 0)
    const safePageSize = Math.max(1, Number(pageSize) || 25)
    const totalPages = Math.max(1, Math.ceil(safeTotal / safePageSize))
    const safePage = Math.min(totalPages, Math.max(1, Number(page) || 1))
    const pages = useMemo(
        () => getPaginationWindow(safePage, totalPages),
        [safePage, totalPages]
    )
    const firstItem = safeTotal === 0 ? 0 : (safePage - 1) * safePageSize + 1
    const lastItem = Math.min(safeTotal, safePage * safePageSize)
    const previousDisabled = disabled || safePage <= 1
    const nextDisabled = disabled || safePage >= totalPages

    return (
        <Box
            component="nav"
            aria-label="Paginação da tabela"
            sx={{
                display: "flex",
                alignItems: {xs: "stretch", md: "center"},
                justifyContent: "space-between",
                flexDirection: {xs: "column", md: "row"},
                gap: 2,
                mt: 2,
            }}
        >
            <Stack spacing={1}>
                <Typography variant="body2" color="primary.white70">
                    {firstItem}-{lastItem} de {safeTotal} registros
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <NavigationButton label="Primeira página" disabled={previousDisabled} onClick={() => onPageChange(1)}>
                        <FirstPageRoundedIcon/>
                    </NavigationButton>
                    <NavigationButton label="Página anterior" disabled={previousDisabled} onClick={() => onPageChange(safePage - 1)}>
                        <KeyboardArrowLeftRoundedIcon/>
                    </NavigationButton>
                    {pages.map((pageNumber) => (
                        <PageButton
                            key={pageNumber}
                            page={pageNumber}
                            active={pageNumber === safePage}
                            disabled={disabled}
                            onClick={() => onPageChange(pageNumber)}
                        />
                    ))}
                    <NavigationButton label="Próxima página" disabled={nextDisabled} onClick={() => onPageChange(safePage + 1)}>
                        <KeyboardArrowRightRoundedIcon/>
                    </NavigationButton>
                    <NavigationButton label="Última página" disabled={nextDisabled} onClick={() => onPageChange(totalPages)}>
                        <LastPageRoundedIcon/>
                    </NavigationButton>
                </Stack>
            </Stack>

            <SelectComponent
                label="Itens por página"
                value={safePageSize}
                disabled={disabled}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                options={pageSizeOptions.map((option) => ({value: Number(option), label: String(option)}))}
                width="150px"
            />
        </Box>
    )
}

TablePagination.propTypes = {
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onPageSizeChange: PropTypes.func.isRequired,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    disabled: PropTypes.bool,
}

export default TablePagination
